import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getDosageFormOptions } from '@/features/internal/drugs/apis/soapTemplate'

interface DosageFormOption {
  value: string
  label: string
}

// デフォルトの剤形オプション（APIが利用できない場合のフォールバック）
const DEFAULT_DOSAGE_FORM_OPTIONS: DosageFormOption[] = [
  { value: 'oral', label: '内服薬' },
  { value: 'injection', label: '注射薬' },
  { value: 'external', label: '外用薬' },
]

interface UseDosageFormOptionsQueryReturn {
  /**
   * 剤形オプションリスト
   */
  dosageFormOptions: DosageFormOption[]

  /**
   * ローディング状態
   */
  isLoading: boolean

  /**
   * エラー
   */
  error: Error | null
}

/**
 * 剤形オプション取得クエリフック
 *
 * 責務:
 * - 剤形マスターデータの取得
 * - フォールバックオプションの提供
 * - 長めのキャッシュ管理（マスターデータのため）
 *
 * 使用例:
 * ```tsx
 * const { dosageFormOptions, isLoading } = useDosageFormOptionsQuery()
 * ```
 */
export const useDosageFormOptionsQuery =
  (): UseDosageFormOptionsQueryReturn => {
    const query = useQuery({
      queryKey: queryKeys.dosageFormOptions.list(),
      queryFn: async () => {
        const response = await getDosageFormOptions()
        if (Array.isArray(response.data.dosage_form_options)) {
          return response.data.dosage_form_options as DosageFormOption[]
        }
        return DEFAULT_DOSAGE_FORM_OPTIONS
      },
      staleTime: 1000 * 60 * 30, // 30分キャッシュ（マスタデータなので長め）
    })

    return {
      dosageFormOptions: query.data ?? DEFAULT_DOSAGE_FORM_OPTIONS,
      isLoading: query.isLoading,
      error: query.error ?? null,
    }
  }
